import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useNavigate, Navigate, useLocation, useParams, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from "@/components/theme-provider"
import { AuthProvider, useAuth } from '@/contexts/auth';
import { RouteFallback } from '@/components/RouteFallback';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { TermlyConsentBridge } from '@/components/TermlyConsentBridge';
import { Loader2 } from 'lucide-react';
import { sanitizeLanguage, setStoredLanguage } from '@/lib/i18n-safe';
import i18n from '@/i18n/config';
import { useHreflang } from '@/hooks/useHreflang';

// Route-level code splitting (P0-4) — each page is its own chunk.
// Named exports are re-shaped to default exports for React.lazy.
const LandingPageV2 = lazy(() =>
  import('@/components/layout/landing-page-v2').then((m) => ({ default: m.LandingPageV2 })),
);
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const LessonList = lazy(() => import('@/pages/LessonList'));
const LessonDetail = lazy(() => import('@/pages/LessonDetail'));
const Profile = lazy(() => import('@/pages/Profile'));
const Login = lazy(() => import('@/pages/Login'));
const Signup = lazy(() => import('@/pages/Signup'));
const AuthCallback = lazy(() =>
  import('@/pages/AuthCallback').then((m) => ({ default: m.AuthCallback })),
);
const CookiePolicy = lazy(() => import('@/pages/CookiePolicy'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));

/**
 * Wraps public routes under /:lang.
 * Sets language synchronously before any child renders to avoid flash of wrong language.
 * INTENTIONALLY synchronous — do NOT move localStorage/changeLanguage to useEffect.
 * useEffect fires after render, causing a visible flash of the previous language.
 */
function PublicLanguageWrapper() {
  const { lang } = useParams<{ lang: string }>();
  const safeLang = sanitizeLanguage(lang);
  useHreflang(safeLang);

  // Synchronous side-effects — intentional, not a React anti-pattern here.
  // Language must be set before children render to prevent flash.
  setStoredLanguage(safeLang);
  if (i18n.language !== safeLang) {
    i18n.changeLanguage(safeLang);
  }

  // Redirect invalid lang segments (e.g. /de/ → /en/)
  if (lang !== safeLang) {
    return <Navigate to={`/${safeLang}`} replace />;
  }

  return <Outlet />;
}

// Protected Route Wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#050505]">
        <Loader2 className="w-10 h-10 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const lang = sanitizeLanguage(i18n.language);
    return <Navigate to={`/${lang}/login`} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n: i18nHook } = useTranslation();

  // document.documentElement.lang is also updated by i18n.on('languageChanged') in config.ts.
  // This effect handles the initial render and any changes not caught by the event.
  useEffect(() => {
    document.documentElement.lang = i18nHook.language || 'en';
  }, [i18nHook.language]);

  // Global listener for 401/403 API responses
  useEffect(() => {
    const handleUnauthorized = () => {
      const lang = sanitizeLanguage(i18n.language);
      navigate(`/${lang}/login`, { state: { from: location.pathname }, replace: true });
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [navigate, location.pathname]);

  return (
    <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
      <TermlyConsentBridge />
      <AuthProvider>
        <RouteErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Root → redirect to default language landing */}
              <Route path="/" element={<Navigate to="/en" replace />} />

              {/* Public routes — language driven by URL prefix */}
              <Route path="/:lang" element={<PublicLanguageWrapper />}>
                <Route index element={<LandingPageV2 onEnter={() => navigate('/dashboard')} />} />
                <Route path="login" element={<Login />} />
                <Route path="signup" element={<Signup />} />
              </Route>

              {/* PKCE magic link / OAuth callback */}
              <Route path="/auth/callback" element={<AuthCallback />} />

              {/* Backward-compat: old flat links redirect to English equivalent */}
              <Route path="/login" element={<Navigate to="/en/login" replace />} />
              <Route path="/signup" element={<Navigate to="/en/signup" replace />} />

              {/* Protected Routes — language driven by localStorage / server preference */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />

              <Route path="/lessons" element={
                <ProtectedRoute>
                  <LessonList />
                </ProtectedRoute>
              } />

              <Route path="/lessons/:slug" element={
                <ProtectedRoute>
                  <LessonDetail />
                </ProtectedRoute>
              } />

              <Route path="/profile" element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } />

              {/* Legal pages — public, no auth required */}
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
